// Merging two duplicate accounts (MOTHER-167; Cameron's card on OLYAA-131): what
// is the accounts' own in the merge (recordMerge.tsx does the rest, as it does
// for contacts) over POST /api/merge/accounts (apps/server/src/mergeRecords.mjs).
import type { MergeRecord } from '@olytic/foundation';

import { accounts } from '../config/objects/accounts';
import { formatMoney, formatNumber, humanize } from './format';
import type { ObjectRow } from './objectsApi';
import { type MergeSpec, mergedRow, shown, textOf } from './recordMerge';

/** What merging does, as the dialog says it (the PM's wording, MOTHER-167). */
export const ACCOUNT_MERGE_CONSEQUENCES = [
  'Everything linked to the account that is merged away moves to the one you keep: contacts, opportunities, projects, quotes, invoices and billing.',
  'The other account goes to the Recycle Bin for 30 days.',
  'Restoring it brings back the account only, not what moved.',
];

const number = (value: unknown) => formatNumber(value);

/**
 * The fields compared, in the record page's order and formatted as it shows
 * them (by their type in /meta: a picklist humanized, a number grouped, Annual
 * Revenue as money; HQ State as it is): the ones it shows that a merge may take
 * from the other account (the server's ACCOUNT_MERGE_FIELDS). Created, Last
 * Modified and the Apollo fields are not people's choices. Company Size is
 * derived from Employees and Annual Revenue, so it is never taken: it shows in
 * each account's subtitle instead (mergeRecordOf).
 */
const MERGED_FIELDS = [
  { key: 'name', format: String },
  { key: 'status', format: humanize },
  { key: 'domain', format: String },
  { key: 'industry', format: humanize },
  { key: 'hq_state', format: String },
  { key: 'icp_score', format: number },
  { key: 'primary_contact_name', format: String },
  { key: 'primary_contact_email', format: String },
  { key: 'employee_count', format: number },
  { key: 'annual_revenue', format: formatMoney },
  { key: 'notes', format: String },
];

/** The record page's label for a field (Title Case, as the page shows it). */
function labelOf(key: string): string {
  const label = accounts.details?.find((field) => field.key === key)?.label;
  return typeof label === 'string' ? label : key;
}

const accountName = (row: ObjectRow): string => textOf(row.name) || 'Unnamed account';

/**
 * An account as the dialog lists it: its name, with its domain and its Company
 * Size, which a merge cannot take (it follows Employees and Annual Revenue).
 */
function mergeRecordOf(row: ObjectRow): MergeRecord {
  const size = shown(String, row.company_size);
  const subtitle = [textOf(row.domain), size === null ? '' : `${labelOf('company_size')}: ${size}`].filter(Boolean).join(' · ');
  return { id: row.id, name: accountName(row), ...(subtitle ? { subtitle } : {}) };
}

/** The kept account's name once merged. */
export function mergedAccountName(survivor: ObjectRow, merged: ObjectRow, takeFromOther: string[]): string {
  return accountName(mergedRow(survivor, merged, takeFromOther, ['name']));
}

export const ACCOUNT_MERGE: MergeSpec = {
  object: 'accounts',
  url: '/api/merge/accounts',
  singular: accounts.singular,
  consequences: ACCOUNT_MERGE_CONSEQUENCES,
  withAnotherLabel: 'Merge with Another Account…',
  fields: MERGED_FIELDS,
  labelOf,
  recordOf: mergeRecordOf,
  mergedName: mergedAccountName,
};
